import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import './parking-scheme-page.scss';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import { Button, Icons } from '../../../components/base/BootstrapComponents';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { loadZoneList } from '../../Zones/actions';
import { getZoneData } from '../../Zones/reducer';

const DEVICE_TYPES = [
  { id: 'barrier', title: 'Шлагбаум', icon: 'barrier' },
  { id: 'lpn_camera', title: 'Камера ГРЗ', icon: 'lpn_camera' },
  { id: 'pay_machine', title: 'Паркомат', icon: 'pay_machine' },
  { id: 'ticket_printer', title: 'Стойка выдачи', icon: 'ticket_printer' },
  { id: 'intercom', title: 'Интерком', icon: 'intercom' },
];

const STATUSES = [
  { id: 'online', label: 'В работе', short: 'OK' },
  { id: 'warning', label: 'Требует внимания', short: 'WARN' },
  { id: 'error', label: 'Критическая ошибка', short: 'ERR' },
  { id: 'service', label: 'Сервисный режим', short: 'SVC' },
];

const AREA_TYPES = [
  {
    id: 'parking',
    title: 'Контур парковки',
    shortTitle: 'Парковка',
    className: 'area-parking',
  },
  {
    id: 'zone',
    title: 'Зона парковки',
    shortTitle: 'Зона',
    className: 'area-zone',
  },
  {
    id: 'entry_exit',
    title: 'Въезд / выезд',
    shortTitle: 'Въезд/выезд',
    className: 'area-entry-exit',
  },
  {
    id: 'technical_object',
    title: 'Тех. объект',
    shortTitle: 'Тех. объект',
    className: 'area-technical-object',
  },
];

const FLOW_DIRECTIONS = [
  { id: 'entry', title: 'Въезд', shortTitle: 'Въезд' },
  { id: 'exit', title: 'Выезд', shortTitle: 'Выезд' },
  { id: 'both', title: 'Въезд и выезд', shortTitle: 'Въезд/выезд' },
];

const BINDING_ENTITIES = [
  {
    id: 'host-entry-01', name: 'HOST-ENT-01', type: 'Хост въезда', zone: 'P1 Север',
  },
  {
    id: 'host-exit-01', name: 'HOST-EXT-01', type: 'Хост выезда', zone: 'P1 Юг',
  },
  {
    id: 'zone-p1-north', name: 'P1 Север', type: 'Парковочная зона', zone: '144 места',
  },
  {
    id: 'zone-p1-south', name: 'P1 Юг', type: 'Парковочная зона', zone: '96 мест',
  },
  {
    id: 'cam-overview-03', name: 'CAM-OVR-03', type: 'Обзорная камера', zone: 'Центральный проезд',
  },
  {
    id: 'pay-terminal-02', name: 'PAY-02', type: 'Платежный терминал', zone: 'Вестибюль A',
  },
];

const INITIAL_DEVICES = [
  {
    id: 'dev-1', type: 'barrier', title: 'Шлагбаум въезда', x: 146, y: 458, status: 'online', entityId: 'host-entry-01', zone: 'P1 Север',
  },
  {
    id: 'dev-2', type: 'lpn_camera', title: 'Камера въезда', x: 218, y: 418, status: 'online', entityId: 'host-entry-01', zone: 'P1 Север',
  },
  {
    id: 'dev-3', type: 'barrier', title: 'Шлагбаум выезда', x: 794, y: 458, status: 'warning', entityId: 'host-exit-01', zone: 'P1 Юг',
  },
  {
    id: 'dev-4', type: 'lpn_camera', title: 'Камера выезда', x: 728, y: 418, status: 'online', entityId: 'host-exit-01', zone: 'P1 Юг',
  },
  {
    id: 'dev-5', type: 'pay_machine', title: 'Паркомат A', x: 524, y: 176, status: 'service', entityId: 'pay-terminal-02', zone: 'Вестибюль A',
  },
  {
    id: 'dev-6', type: 'intercom', title: 'Интерком въезда', x: 110, y: 385, status: 'error', entityId: 'host-entry-01', zone: 'P1 Север',
  },
  {
    id: 'dev-7', type: 'ticket_printer', title: 'Стойка выдачи', x: 284, y: 460, status: 'online', entityId: 'host-entry-01', zone: 'P1 Север',
  },
  {
    id: 'dev-8', type: 'lpn_camera', title: 'Обзорная камера', x: 470, y: 345, status: 'online', entityId: 'cam-overview-03', zone: 'Центральный проезд',
  },
];

const INITIAL_AREAS = [
  {
    id: 'area-parking-main',
    type: 'parking',
    title: 'Контур парковки',
    points: [
      { x: 76, y: 86 },
      { x: 884, y: 76 },
      { x: 856, y: 502 },
      { x: 108, y: 488 },
      { x: 76, y: 332 },
    ],
    placesCount: 0,
  },
  {
    id: 'zone-north',
    type: 'zone',
    title: 'P1 Север',
    points: [
      { x: 116, y: 106 },
      { x: 468, y: 96 },
      { x: 446, y: 346 },
      { x: 104, y: 356 },
    ],
    placesCount: 144,
  },
  {
    id: 'zone-south',
    type: 'zone',
    title: 'P1 Юг',
    points: [
      { x: 508, y: 100 },
      { x: 854, y: 112 },
      { x: 838, y: 354 },
      { x: 494, y: 346 },
    ],
    placesCount: 96,
  },
];

const CLOSE_POLYGON_DISTANCE = 16;
const PARKING_SCHEME_STORAGE_KEY = 'parking-scheme-editor-state';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDistance = (pointA, pointB) => Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);

const getStatusMeta = status => STATUSES.find(item => item.id === status) || STATUSES[0];

const getTypeMeta = type => DEVICE_TYPES.find(item => item.id === type) || DEVICE_TYPES[0];

const getAreaTypeMeta = type => AREA_TYPES.find(item => item.id === type) || AREA_TYPES[1];

const getFlowDirectionMeta = direction => FLOW_DIRECTIONS.find(item => item.id === direction) || FLOW_DIRECTIONS[0];

const getPointsString = points => points.map(point => `${point.x},${point.y}`).join(' ');

const getBoundZoneForArea = (area, zonesById) => (
  area?.boundZoneId ? zonesById[String(area.boundZoneId)] || null : null
);

const getAreaDisplayTitle = (area, zonesById) => getBoundZoneForArea(area, zonesById)?.name || area.title;

const getAreaDisplayPlacesCount = (area, zonesById) => (
  area.type === 'zone'
    ? Number(getBoundZoneForArea(area, zonesById)?.total_spaces ?? 0)
    : Number(area.placesCount || 0)
);

const getSavedParkingScheme = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const savedScheme = window.localStorage.getItem(PARKING_SCHEME_STORAGE_KEY);

    if (!savedScheme) {
      return null;
    }

    const parsedScheme = JSON.parse(savedScheme);

    return {
      devices: Array.isArray(parsedScheme.devices) ? parsedScheme.devices : INITIAL_DEVICES,
      areas: Array.isArray(parsedScheme.areas) ? parsedScheme.areas : INITIAL_AREAS,
      activeLevel: parsedScheme.activeLevel || 'P1',
      lastSavedAt: parsedScheme.lastSavedAt || '12:44',
    };
  } catch {
    return null;
  }
};

const addVector = (point, vector, scale = 1) => ({
  x: point.x + (vector.x * scale),
  y: point.y + (vector.y * scale),
});

const getAreaBounds = points => {
  const xValues = points.map(point => point.x);
  const yValues = points.map(point => point.y);
  const minX = Math.min(...xValues);
  const minY = Math.min(...yValues);
  const maxX = Math.max(...xValues);
  const maxY = Math.max(...yValues);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const getAreaCenter = points => {
  const bounds = getAreaBounds(points);
  return {
    x: bounds.x + (bounds.width / 2),
    y: bounds.y + (bounds.height / 2),
  };
};

const getPolygonArea = points => {
  if (!points || points.length < 3) {
    return 0;
  }

  const rawArea = points.reduce((result, point, index) => {
    const nextPoint = points[(index + 1) % points.length];
    return result + ((point.x * nextPoint.y) - (nextPoint.x * point.y));
  }, 0);

  return Math.abs(rawArea / 2);
};

const isPointInsidePolygon = (point, polygonPoints) => {
  let inside = false;

  for (let index = 0, previousIndex = polygonPoints.length - 1; index < polygonPoints.length; previousIndex = index, index += 1) {
    const currentPoint = polygonPoints[index];
    const previousPoint = polygonPoints[previousIndex];
    const intersects = ((currentPoint.y > point.y) !== (previousPoint.y > point.y))
      && (point.x < ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) / (previousPoint.y - currentPoint.y) + currentPoint.x);

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const getGateGeometry = (areaPoints, gatePoint) => {
  if (!areaPoints || areaPoints.length < 2) {
    return null;
  }

  const center = getAreaCenter(areaPoints);
  const bestSegment = areaPoints.reduce((bestMatch, startPoint, index) => {
    const endPoint = areaPoints[(index + 1) % areaPoints.length];
    const segmentX = endPoint.x - startPoint.x;
    const segmentY = endPoint.y - startPoint.y;
    const segmentLengthSquared = (segmentX * segmentX) + (segmentY * segmentY);
    const progress = segmentLengthSquared
      ? clamp(((gatePoint.x - startPoint.x) * segmentX + (gatePoint.y - startPoint.y) * segmentY) / segmentLengthSquared, 0, 1)
      : 0;
    const edgePoint = {
      x: startPoint.x + (segmentX * progress),
      y: startPoint.y + (segmentY * progress),
    };
    const distance = getDistance(gatePoint, edgePoint);

    if (!bestMatch || distance < bestMatch.distance) {
      return {
        edgePoint,
        distance,
        segmentIndex: index,
        progress,
        segmentX,
        segmentY,
      };
    }

    return bestMatch;
  }, null);

  if (!bestSegment) {
    return null;
  }

  const segmentLength = Math.hypot(bestSegment.segmentX, bestSegment.segmentY) || 1;
  const tangent = {
    x: bestSegment.segmentX / segmentLength,
    y: bestSegment.segmentY / segmentLength,
  };
  const normal = { x: -tangent.y, y: tangent.x };
  const toCenter = {
    x: center.x - bestSegment.edgePoint.x,
    y: center.y - bestSegment.edgePoint.y,
  };
  const inward = ((normal.x * toCenter.x) + (normal.y * toCenter.y)) >= 0
    ? normal
    : { x: -normal.x, y: -normal.y };
  const outward = { x: -inward.x, y: -inward.y };

  return {
    edgePoint: bestSegment.edgePoint,
    segmentIndex: bestSegment.segmentIndex,
    progress: bestSegment.progress,
    segmentLength,
    tangent,
    inward,
    outward,
  };
};

const getGateBorderPoints = (areaPoints, gate) => {
  const geometry = getGateGeometry(areaPoints, gate.point);

  if (!geometry) {
    return null;
  }

  const {
    edgePoint, tangent, inward, outward, segmentIndex, progress, segmentLength,
  } = geometry;
  const desiredHalfWidth = 30;
  const halfWidth = Math.min(desiredHalfWidth, Math.max(12, (segmentLength / 2) - 10));
  const depth = 30;
  const flowDirection = gate.flowDirection || 'entry';
  const isExit = flowDirection === 'exit';
  const isBoth = flowDirection === 'both';
  const directionVector = isExit ? outward : inward;
  const clampedProgress = clamp(progress, halfWidth / segmentLength, 1 - (halfWidth / segmentLength));
  const gateCenter = addVector(areaPoints[segmentIndex], tangent, segmentLength * clampedProgress);
  const points = isBoth ? [
    addVector(gateCenter, tangent, -halfWidth),
    addVector(gateCenter, inward, depth),
    addVector(gateCenter, outward, depth),
    addVector(gateCenter, tangent, halfWidth),
  ] : [
    addVector(gateCenter, tangent, -halfWidth),
    addVector(gateCenter, directionVector, depth),
    addVector(gateCenter, tangent, halfWidth),
  ];

  return {
    segmentIndex,
    progress: clampedProgress,
    points,
  };
};

const getAreaRenderPoints = area => {
  if (!area.gates?.length) {
    return area.points;
  }

  const gatesBySegment = area.gates.reduce((result, gate) => {
    const gateBorder = getGateBorderPoints(area.points, gate);

    if (!gateBorder) {
      return result;
    }

    return {
      ...result,
      [gateBorder.segmentIndex]: [...(result[gateBorder.segmentIndex] || []), gateBorder],
    };
  }, {});

  return area.points.reduce((result, point, index) => {
    const segmentGates = (gatesBySegment[index] || [])
      .sort((firstGate, secondGate) => firstGate.progress - secondGate.progress);

    return [
      ...result,
      point,
      ...segmentGates.flatMap(gate => gate.points),
    ];
  }, []);
};

function ParkingArea({
  area, selected, activeTool, parkingCenter, zonesById, onSelectArea,
}) {
  const areaTypeMeta = getAreaTypeMeta(area.type);
  const renderPoints = getAreaRenderPoints(area);
  const center = getAreaCenter(renderPoints);
  const bounds = getAreaBounds(renderPoints);
  const shouldShowCapacity = area.type === 'zone';
  const displayTitle = getAreaDisplayTitle(area, zonesById);
  const displayPlacesCount = getAreaDisplayPlacesCount(area, zonesById);
  const titleX = shouldShowCapacity ? center.x : bounds.x + 14;
  const titleY = shouldShowCapacity ? center.y - 10 : bounds.y + 28;
  const flowDirection = area.flowDirection || 'entry';
  const directionAngle = parkingCenter
    ? Math.atan2(parkingCenter.y - center.y, parkingCenter.x - center.x) * (180 / Math.PI)
    : 0;
  const directionItems = flowDirection === 'both'
    ? [
      { id: 'entry', label: 'Въезд', angle: directionAngle },
      { id: 'exit', label: 'Выезд', angle: directionAngle + 180 },
    ]
    : [{ id: flowDirection, label: getFlowDirectionMeta(flowDirection).shortTitle, angle: directionAngle + (flowDirection === 'exit' ? 180 : 0) }];

  return (
    <g
      className={classNames('scheme-area-group', areaTypeMeta.className, { selected })}
      onPointerDown={event => {
        if (activeTool !== 'select') {
          return;
        }
        event.stopPropagation();
        onSelectArea(area.id);
      }}
    >
      <polygon className="scheme-area" points={getPointsString(renderPoints)} />
      <text className="scheme-area-title" x={titleX} y={titleY} textAnchor={shouldShowCapacity ? 'middle' : 'start'}>{displayTitle}</text>
      {area.type === 'entry_exit' ? (
        <g className={classNames('scheme-direction', `direction-${flowDirection}`)}>
          {directionItems.map((item, index) => (
            <g
              key={item.id}
              className={`scheme-direction-row ${item.id}`}
              transform={`translate(${center.x}, ${center.y + ((index - ((directionItems.length - 1) / 2)) * 26)}) rotate(${item.angle})`}
            >
              <path className="scheme-direction-line" d="M-38 0H38" markerEnd={`url(#lane-arrow-${item.id === 'exit' ? 'exit' : 'entry'})`} />
              <text transform={`rotate(${-item.angle})`} x="0" y="-10" textAnchor="middle">{item.label}</text>
            </g>
          ))}
        </g>
      ) : null}
      {shouldShowCapacity ? (
        <g className="scheme-area-capacity" transform={`translate(${center.x}, ${center.y + 8})`}>
          <rect x="-52" width="104" height="25" rx="5" />
          <text x="0" y="17" textAnchor="middle">{displayPlacesCount} мест</text>
        </g>
      ) : null}
    </g>
  );
}

function ParkingMap({
  devices,
  areas,
  zonesById,
  selectedDeviceId,
  selectedAreaId,
  editorMode,
  activeTool,
  onSelectDevice,
  onSelectArea,
  onMoveDevice,
  onAddDraftPoint,
  onFinishDraftArea,
  draftAreaPoints,
}) {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [hoverPoint, setHoverPoint] = useState(null);

  const getPoint = event => {
    const svg = svgRef.current;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const svgPoint = point.matrixTransform(svg.getScreenCTM().inverse());
    return {
      x: clamp(Math.round(svgPoint.x), 38, 922),
      y: clamp(Math.round(svgPoint.y), 40, 520),
    };
  };

  const handlePointerDown = device => event => {
    event.stopPropagation();
    onSelectDevice(device.id);

    if (editorMode !== 'edit') {
      return;
    }

    event.preventDefault();
    const point = getPoint(event);
    setDragging({
      type: 'device',
      id: device.id,
      offsetX: point.x - device.x,
      offsetY: point.y - device.y,
    });
  };

  const handleCanvasPointerDown = event => {
    if (editorMode !== 'edit' || activeTool === 'select') {
      return;
    }

    const point = getPoint(event);
    onAddDraftPoint(point);
  };

  const handleCanvasDoubleClick = event => {
    if (editorMode !== 'edit' || activeTool === 'select') {
      return;
    }

    event.preventDefault();
    onFinishDraftArea();
  };

  const handlePointerMove = event => {
    if (!dragging) {
      setHoverPoint(getPoint(event));
      return;
    }

    const point = getPoint(event);
    const nextPoint = {
      x: clamp(Math.round(point.x - dragging.offsetX), 58, 890),
      y: clamp(Math.round(point.y - dragging.offsetY), 72, 504),
    };

    onMoveDevice(dragging.id, nextPoint);
  };

  const stopDragging = () => {
    setDragging(null);
  };

  const canCloseDraft = draftAreaPoints.length >= 3
    && hoverPoint
    && getDistance(hoverPoint, draftAreaPoints[0]) <= CLOSE_POLYGON_DISTANCE;
  const draftPreviewPoints = draftAreaPoints.length && hoverPoint
    ? [...draftAreaPoints, canCloseDraft ? draftAreaPoints[0] : hoverPoint]
    : draftAreaPoints;
  const parkingArea = areas.find(area => area.type === 'parking');
  const parkingCenter = parkingArea ? getAreaCenter(parkingArea.points) : { x: 480, y: 280 };

  return (
    <svg
      ref={svgRef}
      className="scheme-canvas"
      viewBox="0 0 960 560"
      role="img"
      aria-label="Редактор мнемосхемы парковки"
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerLeave={stopDragging}
      onPointerDown={handleCanvasPointerDown}
      onDoubleClick={handleCanvasDoubleClick}
    >
      <defs>
        <pattern id="scheme-grid" width="32" height="32" patternUnits="userSpaceOnUse">
          <path d="M32 0H0V32" fill="none" stroke="#e8eef6" strokeWidth="1" />
        </pattern>
        <marker id="lane-arrow-entry" viewBox="0 0 12 12" refX="10" refY="6" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
          <path d="M2 2L10 6L2 10Z" fill="#eaf5f6" />
        </marker>
        <marker id="lane-arrow-exit" viewBox="0 0 12 12" refX="10" refY="6" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
          <path d="M2 2L10 6L2 10Z" fill="#ff4a51" />
        </marker>
      </defs>

      <rect className="scheme-bg" width="960" height="560" />
      <rect className="scheme-grid" width="960" height="560" />

      {areas.map(area => (
        <ParkingArea
          key={area.id}
          area={area}
          selected={area.id === selectedAreaId}
          activeTool={activeTool}
          parkingCenter={parkingCenter}
          zonesById={zonesById}
          onSelectArea={onSelectArea}
        />
      ))}
      {draftPreviewPoints.length ? (
        <g className="scheme-area-draft">
          {draftPreviewPoints.length > 2 ? <polygon points={getPointsString(draftPreviewPoints)} /> : null}
          <polyline points={getPointsString(draftPreviewPoints)} />
          {draftAreaPoints.map((point, index) => (
            <g
              key={`${point.x}-${point.y}-${index}`}
              className={classNames('scheme-area-point', { closing: index === 0 && canCloseDraft })}
              transform={`translate(${point.x}, ${point.y})`}
            >
              <circle r="6" />
              <text y="-10" textAnchor="middle">{index + 1}</text>
            </g>
          ))}
        </g>
      ) : null}

      {devices.map((device, index) => {
        const typeMeta = getTypeMeta(device.type);
        const statusMeta = getStatusMeta(device.status);
        const selected = device.id === selectedDeviceId;
        const deviceNumber = String(index + 1).padStart(2, '0');

        return (
          <g
            key={device.id}
            className={classNames('scheme-device', `status-${device.status}`, { selected, movable: editorMode === 'edit' })}
            transform={`translate(${device.x}, ${device.y})`}
            onPointerDown={handlePointerDown(device)}
          >
            <rect className="scheme-device-hit" x="-28" y="-44" width="56" height="84" rx="6" />
            <rect className="scheme-device-card" x="-24" y="-38" width="48" height="70" rx="4" />
            <rect className="scheme-device-screen" x="-10" y="-22" width="20" height="18" rx="2" />
            <svg x="-10" y="-22" width="20" height="20" className="scheme-device-icon">
              <use xlinkHref={`${process.env.PUBLIC_URL}/icons/custom-icons.svg#${typeMeta.icon}`} />
            </svg>
            <text className="scheme-device-number" y="12" textAnchor="middle">{deviceNumber}</text>
            <rect className="scheme-device-footer" x="-24" y="18" width="48" height="14" rx="0" />
            <text className="scheme-device-label" y="29" textAnchor="middle">{statusMeta.short}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function ParkingSchemePage() {
  const dispatch = useDispatch();
  const pageRef = useRef(null);
  const { zoneList } = useSelector(getZoneData);
  const [savedScheme] = useState(getSavedParkingScheme);
  const [devices, setDevices] = useState(savedScheme?.devices || INITIAL_DEVICES);
  const [areas, setAreas] = useState(savedScheme?.areas || INITIAL_AREAS);
  const [selectedDeviceId, setSelectedDeviceId] = useState(savedScheme?.devices?.[0]?.id || INITIAL_DEVICES[0].id);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [editorMode, setEditorMode] = useState('edit');
  const [activeTool, setActiveTool] = useState('select');
  const [activeGateDirection, setActiveGateDirection] = useState('entry');
  const [draftAreaPoints, setDraftAreaPoints] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(savedScheme?.lastSavedAt || '12:44');

  const selectedDevice = useMemo(
    () => devices.find(device => device.id === selectedDeviceId) || null,
    [devices, selectedDeviceId],
  );
  const selectedArea = useMemo(
    () => areas.find(area => area.id === selectedAreaId) || null,
    [areas, selectedAreaId],
  );
  const zonesById = useMemo(
    () => zoneList.reduce((result, zone) => ({ ...result, [String(zone.id)]: zone }), {}),
    [zoneList],
  );

  const totalPlacesCount = useMemo(
    () => areas.reduce((result, area) => result + (area.type === 'zone' ? getAreaDisplayPlacesCount(area, zonesById) : 0), 0),
    [areas, zonesById],
  );

  const deviceStats = useMemo(() => STATUSES.map(status => ({
    ...status,
    count: devices.filter(device => device.status === status.id).length,
  })), [devices]);

  const zoneOptions = useMemo(
    () => areas.filter(area => area.type === 'zone').map(area => getAreaDisplayTitle(area, zonesById)),
    [areas, zonesById],
  );
  const selectedAreaBoundZone = selectedArea ? getBoundZoneForArea(selectedArea, zonesById) : null;
  const availableZoneBindings = useMemo(
    () => zoneList.filter(zone => !areas.some(area => (
      area.id !== selectedArea?.id
      && area.type === 'zone'
      && String(area.boundZoneId || '') === String(zone.id)
    ))),
    [areas, selectedArea?.id, zoneList],
  );
  const parkingArea = useMemo(
    () => areas.find(area => area.type === 'parking') || null,
    [areas],
  );
  const zonePolygonCount = useMemo(
    () => areas.filter(area => area.type === 'zone').length,
    [areas],
  );

  useEffect(() => {
    dispatch(loadZoneList()).catch(() => {});
  }, [dispatch]);

  const notifyParkingRequired = () => {
    notificationDecorator({
      isWarning: true,
      title: 'Сначала создайте парковку',
      message: 'Зону можно рисовать только внутри контура парковки.',
    });
  };

  const notifyZoneLimitReached = () => {
    notificationDecorator({
      isWarning: true,
      title: 'Недостаточно зон',
      message: zoneList.length
        ? 'Количество полигонов зон не может быть больше количества реальных зон.'
        : 'Нельзя рисовать полигоны зон, пока в системе нет ни одной зоны.',
    });
  };

  const activateTool = nextTool => {
    if (nextTool === 'zone') {
      if (!parkingArea) {
        notifyParkingRequired();
        return;
      }

      if (zonePolygonCount >= zoneList.length) {
        notifyZoneLimitReached();
        return;
      }
    }

    setEditorMode('edit');
    setActiveTool(nextTool);
    cancelDraftArea();
  };

  const updateDevice = (id, patch) => {
    setDevices(currentDevices => currentDevices.map(device => (
      device.id === id ? { ...device, ...patch } : device
    )));
  };

  const updateArea = (id, patch) => {
    setAreas(currentAreas => currentAreas.map(area => (
      area.id === id ? { ...area, ...patch } : area
    )));
  };

  const handleSelectDevice = id => {
    setSelectedDeviceId(id);
    setSelectedAreaId(null);
  };

  const handleSelectArea = id => {
    setSelectedAreaId(id);
    setSelectedDeviceId(null);
  };

  const cancelDraftArea = () => {
    setDraftAreaPoints([]);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key !== 'Escape' || activeTool === 'select') {
        return;
      }

      setActiveTool('select');
      cancelDraftArea();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool]);

  useEffect(() => {
    const pageElement = pageRef.current;
    const contentElement = pageElement?.closest('.content');
    const mainLayoutElement = pageElement?.closest('.main-layout');

    if (!contentElement || !mainLayoutElement) {
      return undefined;
    }

    contentElement.classList.add('parking-scheme-content');
    mainLayoutElement.classList.add('parking-scheme-layout');

    return () => {
      contentElement.classList.remove('parking-scheme-content');
      mainLayoutElement.classList.remove('parking-scheme-layout');
    };
  }, []);

  const finishDraftArea = (points = draftAreaPoints) => {
    if (points.length < 3 || activeTool === 'select' || activeTool === 'entry_exit') {
      return;
    }

    if (activeTool === 'zone') {
      if (!parkingArea) {
        notifyParkingRequired();
        return;
      }

      if (zonePolygonCount >= zoneList.length) {
        notifyZoneLimitReached();
        return;
      }

      const isInsideParking = points.every(point => isPointInsidePolygon(point, parkingArea.points));

      if (!isInsideParking) {
        notificationDecorator({
          isWarning: true,
          title: 'Зона вне парковки',
          message: 'Все точки зоны должны находиться внутри контура парковки.',
        });
        return;
      }
    }

    const areaTypeMeta = getAreaTypeMeta(activeTool);
    const sameTypeCount = areas.filter(area => area.type === activeTool).length + 1;
    const newArea = {
      id: `area-${Date.now()}`,
      type: activeTool,
      title: `${areaTypeMeta.shortTitle} ${sameTypeCount}`,
      points,
      placesCount: 0,
    };

    setAreas(currentAreas => [...currentAreas, newArea]);
    setSelectedAreaId(newArea.id);
    setSelectedDeviceId(null);
    setDraftAreaPoints([]);
  };

  const addGateToArea = point => {
    const targetArea = areas
      .filter(area => area.type === 'zone' && isPointInsidePolygon(point, area.points))
      .sort((firstArea, secondArea) => getPolygonArea(firstArea.points) - getPolygonArea(secondArea.points))[0];

    if (!targetArea) {
      return;
    }

    const gate = {
      id: `gate-${Date.now()}`,
      point,
      flowDirection: activeGateDirection,
    };

    setAreas(currentAreas => currentAreas.map(area => (
      area.id === targetArea.id
        ? { ...area, gates: [...(area.gates || []), gate] }
        : area
    )));
    setSelectedAreaId(targetArea.id);
    setSelectedDeviceId(null);
  };

  const addDraftPoint = point => {
    if (activeTool === 'entry_exit') {
      addGateToArea(point);
      return;
    }

    if (
      draftAreaPoints.length >= 3
      && getDistance(point, draftAreaPoints[0]) <= CLOSE_POLYGON_DISTANCE
    ) {
      finishDraftArea(draftAreaPoints);
      return;
    }

    setDraftAreaPoints(currentPoints => [...currentPoints, point]);
  };

  const addDevice = type => {
    const typeMeta = getTypeMeta(type);
    const nextIndex = devices.length + 1;
    const defaultZone = zoneOptions[0] || 'P1 Север';
    const newDevice = {
      id: `dev-${Date.now()}`,
      type,
      title: `${typeMeta.title} ${nextIndex}`,
      x: 416 + ((nextIndex % 4) * 48),
      y: 408 + ((nextIndex % 3) * 28),
      status: 'online',
      entityId: BINDING_ENTITIES[0].id,
      zone: defaultZone,
    };

    setDevices(currentDevices => [...currentDevices, newDevice]);
    setSelectedDeviceId(newDevice.id);
    setSelectedAreaId(null);
    setEditorMode('edit');
    setActiveTool('select');
  };

  const removeSelectedDevice = () => {
    if (!selectedDevice) {
      return;
    }

    const nextDevices = devices.filter(device => device.id !== selectedDevice.id);
    setDevices(nextDevices);
    setSelectedDeviceId(nextDevices[0]?.id || null);
  };

  const removeSelectedArea = () => {
    if (!selectedArea) {
      return;
    }

    const nextAreas = areas.filter(area => area.id !== selectedArea.id);
    setAreas(nextAreas);
    setSelectedAreaId(nextAreas[0]?.id || null);
  };

  const updateAreaGate = (areaId, gateId, patch) => {
    setAreas(currentAreas => currentAreas.map(area => {
      if (area.id !== areaId) {
        return area;
      }

      return {
        ...area,
        gates: (area.gates || []).map(gate => (gate.id === gateId ? { ...gate, ...patch } : gate)),
      };
    }));
  };

  const removeAreaGate = (areaId, gateId) => {
    setAreas(currentAreas => currentAreas.map(area => (
      area.id === areaId
        ? { ...area, gates: (area.gates || []).filter(gate => gate.id !== gateId) }
        : area
    )));
  };

  const saveScheme = () => {
    const date = new Date();
    const nextLastSavedAt = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    window.localStorage.setItem(PARKING_SCHEME_STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: date.toISOString(),
      lastSavedAt: nextLastSavedAt,
      areas,
      devices,
    }));
    setLastSavedAt(nextLastSavedAt);
  };

  const selectedEntity = selectedDevice
    ? BINDING_ENTITIES.find(entity => entity.id === selectedDevice.entityId) || BINDING_ENTITIES[0]
    : null;
  return (
    <div className="parking-scheme-page" ref={pageRef}>
      <div className="parking-scheme-container">
        <div className="parking-scheme-header">
          <PageHeader header="Мнемосхема парковки">
            <div className="scheme-save-state">
              <span>Сохранено {lastSavedAt}</span>
              <Button className="scheme-save-button" onClick={saveScheme}>
                <Icons customIcon icon="save-alt" width={18} height={18} />
                Сохранить
              </Button>
            </div>
          </PageHeader>
          <div className="scheme-toolbar" aria-label="Панель редактора мнемосхемы">
            <div className="scheme-segmented">
              <button
                type="button"
                className={classNames({ active: editorMode === 'edit' })}
                onClick={() => {
                  setEditorMode('edit');
                }}
              >
                Редактор
              </button>
              <button
                type="button"
                className={classNames({ active: editorMode === 'view' })}
                onClick={() => setEditorMode('view')}
              >
                Оператор
              </button>
            </div>
            <div className="scheme-segmented scheme-tools">
              <button
                type="button"
                className={classNames({ active: activeTool === 'select' })}
                onClick={() => {
                  setActiveTool('select');
                  cancelDraftArea();
                }}
              >
                Выбор
              </button>
              {AREA_TYPES.map(areaType => (
                <button
                  key={areaType.id}
                  type="button"
                  className={classNames({ active: activeTool === areaType.id })}
                  onClick={() => activateTool(areaType.id)}
                >
                  {areaType.shortTitle}
                </button>
              ))}
            </div>
            {activeTool === 'entry_exit' ? (
              <select
                className="scheme-toolbar-select"
                value={activeGateDirection}
                onChange={event => setActiveGateDirection(event.target.value)}
                aria-label="Направление въезда или выезда"
              >
                {FLOW_DIRECTIONS.map(direction => (
                  <option key={direction.id} value={direction.id}>{direction.title}</option>
                ))}
              </select>
            ) : null}
            {activeTool !== 'select' ? (
              <div className="scheme-toolbar-hint">
                {activeTool === 'entry_exit'
                  ? `${getFlowDirectionMeta(activeGateDirection).title}: выберите границу зоны`
                  : `Точек: ${draftAreaPoints.length}`}
              </div>
            ) : null}
            <div className="scheme-toolbar-spacer" />
            <button type="button" className="scheme-icon-button" title="Центрировать" aria-label="Центрировать схему">
              <Icons customIcon icon="center-map" width={20} height={20} />
            </button>
            <button type="button" className="scheme-icon-button" title="Снимок схемы" aria-label="Сделать снимок схемы">
              <Icons customIcon icon="picture" width={20} height={20} />
            </button>
          </div>
        </div>

        <div className="scheme-workspace">
          <aside className="scheme-panel scheme-palette">
            <div className="scheme-panel-title">Устройства</div>
            <div className="scheme-palette-list">
              {DEVICE_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  className="scheme-palette-item"
                  onClick={() => addDevice(type.id)}
                >
                  <span className="scheme-palette-icon">
                    <Icons customIcon icon={type.icon} width={24} height={24} />
                  </span>
                  <span>{type.title}</span>
                  <Icons customIcon icon="add" width={18} height={18} />
                </button>
              ))}
            </div>

            <div className="scheme-panel-title compact">Состояния</div>
            <div className="scheme-status-grid">
              {deviceStats.map(status => (
                <button
                  key={status.id}
                  type="button"
                  className={classNames('scheme-status-card', `status-${status.id}`)}
                  onClick={() => selectedDevice && updateDevice(selectedDevice.id, { status: status.id })}
                >
                  <span className="scheme-status-dot" />
                  <span>{status.label}</span>
                  <strong>{status.count}</strong>
                </button>
              ))}
            </div>
          </aside>

          <main className="scheme-map-frame">
            <ParkingMap
              devices={devices}
              areas={areas}
              zonesById={zonesById}
              selectedDeviceId={selectedDevice?.id}
              selectedAreaId={selectedArea?.id}
              editorMode={editorMode}
              activeTool={activeTool}
              onSelectDevice={handleSelectDevice}
              onSelectArea={handleSelectArea}
              onMoveDevice={updateDevice}
              onAddDraftPoint={addDraftPoint}
              onFinishDraftArea={finishDraftArea}
              draftAreaPoints={draftAreaPoints}
            />
          </main>

          <aside className="scheme-panel scheme-inspector">
            <div className="scheme-panel-title">
              {selectedArea ? 'Выбранный полигон' : 'Выбранное устройство'}
            </div>
            {selectedArea ? (
              <>
                {selectedArea.type !== 'zone' ? (
                  <label className="scheme-field" htmlFor="scheme-area-title">
                    <span>Название</span>
                    <input
                      id="scheme-area-title"
                      value={getAreaDisplayTitle(selectedArea, zonesById)}
                      onChange={event => updateArea(selectedArea.id, { title: event.target.value })}
                    />
                  </label>
                ) : null}
                <label className="scheme-field" htmlFor="scheme-area-type">
                  <span>Тип</span>
                  <select
                    id="scheme-area-type"
                    value={selectedArea.type}
                    onChange={event => {
                      if (
                        event.target.value === 'zone'
                        && selectedArea.type !== 'zone'
                        && zonePolygonCount >= zoneList.length
                      ) {
                        notifyZoneLimitReached();
                        return;
                      }

                      updateArea(selectedArea.id, {
                        type: event.target.value,
                        placesCount: event.target.value === 'zone' ? selectedArea.placesCount : 0,
                        boundZoneId: event.target.value === 'zone' ? selectedArea.boundZoneId : undefined,
                        flowDirection: event.target.value === 'entry_exit' ? selectedArea.flowDirection || 'entry' : undefined,
                      });
                    }}
                  >
                    {AREA_TYPES.filter(areaType => areaType.id !== 'entry_exit').map(areaType => (
                      <option key={areaType.id} value={areaType.id}>{areaType.title}</option>
                    ))}
                  </select>
                </label>
                {selectedArea.type === 'zone' ? (
                  <label className="scheme-field" htmlFor="scheme-area-zone-binding">
                    <span>Зона</span>
                    <select
                      id="scheme-area-zone-binding"
                      value={selectedArea.boundZoneId || ''}
                      onChange={event => updateArea(selectedArea.id, { boundZoneId: event.target.value || undefined })}
                    >
                      <option value="">Без привязки</option>
                      {availableZoneBindings.map(zone => (
                        <option key={zone.id} value={zone.id}>{zone.name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {selectedArea.type === 'zone' ? (
                  <label className="scheme-field" htmlFor="scheme-area-places">
                    <span>Количество парковочных мест</span>
                    <input
                      id="scheme-area-places"
                      type="number"
                      min="0"
                      value={getAreaDisplayPlacesCount(selectedArea, zonesById)}
                      disabled
                      readOnly
                    />
                  </label>
                ) : null}
                {selectedArea.type === 'zone' ? (
                  <>
                    <div className="scheme-panel-title compact">Въезды и выезды</div>
                    <div className="scheme-gate-list">
                      {(selectedArea.gates || []).length ? selectedArea.gates.map((gate, index) => (
                        <div className="scheme-gate-row" key={gate.id}>
                          <span>{index + 1}</span>
                          <select
                            value={gate.flowDirection || 'entry'}
                            onChange={event => updateAreaGate(selectedArea.id, gate.id, { flowDirection: event.target.value })}
                          >
                            {FLOW_DIRECTIONS.map(direction => (
                              <option key={direction.id} value={direction.id}>{direction.title}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            aria-label={`Удалить проем ${index + 1}`}
                            onClick={() => removeAreaGate(selectedArea.id, gate.id)}
                          >
                            <Icons customIcon icon="delete-outline" width={16} height={16} />
                          </button>
                        </div>
                      )) : (
                        <div className="scheme-gate-empty">Нет проемов</div>
                      )}
                    </div>
                  </>
                ) : null}
                <Button className="scheme-delete-button" variant="secondary" onClick={removeSelectedArea}>
                  <Icons customIcon icon="delete-outline" width={18} height={18} />
                  Удалить полигон
                </Button>
              </>
            ) : null}
            {!selectedArea && selectedDevice ? (
              <>
                <label className="scheme-field" htmlFor="scheme-device-title">
                  <span>Название</span>
                  <input
                    id="scheme-device-title"
                    value={selectedDevice.title}
                    onChange={event => updateDevice(selectedDevice.id, { title: event.target.value })}
                  />
                </label>
                <label className="scheme-field" htmlFor="scheme-device-type">
                  <span>Тип устройства</span>
                  <select
                    id="scheme-device-type"
                    value={selectedDevice.type}
                    onChange={event => updateDevice(selectedDevice.id, { type: event.target.value })}
                  >
                    {DEVICE_TYPES.map(type => <option key={type.id} value={type.id}>{type.title}</option>)}
                  </select>
                </label>
                <label className="scheme-field" htmlFor="scheme-device-zone">
                  <span>Зона парковки</span>
                  <select
                    id="scheme-device-zone"
                    value={selectedDevice.zone}
                    onChange={event => updateDevice(selectedDevice.id, { zone: event.target.value })}
                  >
                    {zoneOptions.map(zoneTitle => <option key={zoneTitle}>{zoneTitle}</option>)}
                  </select>
                </label>
                <label className="scheme-field" htmlFor="scheme-device-status">
                  <span>Статус</span>
                  <select
                    id="scheme-device-status"
                    value={selectedDevice.status}
                    onChange={event => updateDevice(selectedDevice.id, { status: event.target.value })}
                  >
                    {STATUSES.map(status => <option key={status.id} value={status.id}>{status.label}</option>)}
                  </select>
                </label>

                <div className="scheme-binding-summary">
                  <div className={classNames('scheme-binding-status', `status-${selectedDevice.status}`)}>
                    <span />
                    {getStatusMeta(selectedDevice.status).label}
                  </div>
                  <div className="scheme-binding-name">{selectedEntity.name}</div>
                  <div className="scheme-binding-meta">{selectedEntity.type} · {selectedEntity.zone}</div>
                </div>

                <div className="scheme-panel-title compact">Привязка</div>
                <div className="scheme-entity-list">
                  {BINDING_ENTITIES.map(entity => (
                    <button
                      key={entity.id}
                      type="button"
                      className={classNames('scheme-entity-item', { selected: selectedDevice.entityId === entity.id })}
                      onClick={() => updateDevice(selectedDevice.id, { entityId: entity.id, zone: entity.zone.includes('мест') ? selectedDevice.zone : entity.zone })}
                    >
                      <span>{entity.name}</span>
                      <small>{entity.type}</small>
                    </button>
                  ))}
                </div>

                <Button className="scheme-delete-button" variant="secondary" onClick={removeSelectedDevice}>
                  <Icons customIcon icon="delete-outline" width={18} height={18} />
                  Удалить объект
                </Button>
              </>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
